// src/components/VendorShowcase/VendorShowcase.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Rating,
  IconButton,
  Skeleton,
  Container,
  Stack,
  Avatar,
  Divider,
  Paper,
  useTheme,
  alpha,
  Zoom,
  Fade,
  Grow,
  Tooltip,
  LinearProgress,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  MobileStepper,
  SwipeableDrawer,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import {
  LocationOn,
  Verified,
  Star,
  StarBorder,
  LocalShipping,
  Discount,
  TrendingUp,
  Storefront,
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
  FlashOn,
  Inventory,
  AccessTime,
  Favorite,
  FavoriteBorder,
  Share,
  NearMe,
  Grade,
  EmojiEvents,
  LocalOffer,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { NearByVendors } from "../../../services/api/vendor";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

// ─── Styled Components ───────────────────────────────────────────────────────

const VendorCard = styled(Card)(({ theme, verified }) => ({
  background: "#ffffff",
  borderRadius: "24px",
  transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
  cursor: "pointer",
  position: "relative",
  overflow: "visible",
  border: verified
    ? `1px solid rgba(245, 175, 25, 0.35)`
    : "1px solid rgba(0,0,0,0.07)",
  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    transform: "translateY(-12px) scale(1.02)",
    boxShadow: "0 24px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(245,175,25,0.3)",
    "& .vendor-overlay": {
      opacity: 1,
    },
    "& .product-preview": {
      transform: "translateY(-8px)",
    },
  },
}));

const VerifiedBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -12,
  right: 20,
  background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
  borderRadius: "40px",
  padding: "5px 12px",
  display: "flex",
  alignItems: "center",
  gap: 5,
  zIndex: 10,
  boxShadow: "0 4px 16px rgba(245, 175, 25, 0.4)",
  "& .MuiSvgIcon-root": {
    fontSize: 14,
    color: "white",
  },
  "& span": {
    color: "white",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
  },
}));

const ProductPreviewCard = styled(Box)(({ theme }) => ({
  background: "#f5f5f5",
  borderRadius: "12px",
  overflow: "hidden",
  transition: "all 0.3s ease",
  cursor: "pointer",
  position: "relative",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    "& .product-overlay": {
      opacity: 1,
    },
  },
}));

const DiscountChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: 8,
  left: 8,
  background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
  color: "white",
  fontWeight: 800,
  fontSize: "10px",
  height: 22,
  zIndex: 2,
  "& .MuiChip-label": {
    padding: "0 7px",
  },
}));

const FloatingActionButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  bottom: 8,
  right: 8,
  background: "rgba(0,0,0,0.06)",
  backdropFilter: "blur(4px)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
    color: "white",
    transform: "scale(1.1)",
  },
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(6),
  position: "relative",
  "& h2": {
    fontSize: "2.6rem",
    fontWeight: 800,
    color: "#1a1a1a",
    marginBottom: theme.spacing(1),
    letterSpacing: "-0.5px",
    "& span": {
      color: "#f5af19",
    },
  },
  "& .subtitle": {
    color: "#888",
    fontSize: "1rem",
    fontWeight: 500,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -16,
    left: "50%",
    transform: "translateX(-50%)",
    width: 60,
    height: 3,
    background: "linear-gradient(90deg, #e6ba54, #e6ba54)",
    borderRadius: 2,
  },
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  background: "#ffffff",
  borderRadius: "20px",
  padding: theme.spacing(3),
  color: "#1a1a1a",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
  border: "1px solid rgba(245,175,25,0.2)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 150,
    height: 150,
    background: "rgba(245,175,25,0.06)",
    borderRadius: "50%",
  },
}));

// ─── Helper Functions ───────────────────────────────────────────────────────

const formatPrice = (price) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
};

const calculateDiscountPercentage = (mrp, salePrice) => {
  if (!mrp || !salePrice || mrp <= salePrice) return 0;
  return Math.round(((mrp - salePrice) / mrp) * 100);
};

// ─── Product Preview Component ──────────────────────────────────────────────

const ProductPreview = ({ product, onProductClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const discount =
    product.discount ||
    calculateDiscountPercentage(product.mrp, product.salePrice);

  return (
    <Grow in={true} timeout={500}>
      <ProductPreviewCard
        className="product-preview"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onProductClick(product.masterProductUuid)}
      >
        <Box sx={{ position: "relative", paddingTop: "100%" }}>
          <CardMedia
            component="img"
            image={product.imageUrl || "https://via.placeholder.com/200"}
            alt={product.name}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.3s ease",
              transform: isHovered ? "scale(1.1)" : "scale(1)",
            }}
          />

          <Box
            className="product-overlay"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.85) 100%)",
              opacity: 0,
              transition: "opacity 0.3s ease",
              display: "flex",
              alignItems: "flex-end",
              p: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#e6ba54",
                fontWeight: 800,
                fontSize: "9px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              Quick View
            </Typography>
          </Box>
        </Box>
      </ProductPreviewCard>
    </Grow>
  );
};

// ─── Vendor Card Component ──────────────────────────────────────────────────

const VendorCardComponent = ({
  vendor,
  onVendorClick,
  onProductClick,
  index,
}) => {
  const theme = useTheme();
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const featuredProducts = vendor.featuredProducts || [];
  const settings = {
    dots: false,
    infinite: featuredProducts.length > 3,
    speed: 500,
    slidesToShow: Math.min(3, featuredProducts.length),
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: featuredProducts.length > 3,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: Math.min(2, featuredProducts.length) },
      },
      {
        breakpoint: 600,
        settings: { slidesToShow: Math.min(1, featuredProducts.length) },
      },
    ],
  };

  return (
    <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
      <VendorCard
        verified={vendor.isVerified}
        sx={{ height: "100%", display: "flex", flexDirection: "column",width: 400 }}
      >
        {vendor.isVerified && (
          <VerifiedBadge>
            <Verified />
            <span>Verified</span>
          </VerifiedBadge>
        )}

        <CardContent sx={{ p: 2.5, flex: 1 }}>
          {/* Vendor Header */}
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Avatar
              src={vendor.profilePhotoUrl}
              sx={{
                width: 64,
                height: 64,
                borderRadius: "18px",
                border: "2px solid rgba(245,175,25,0.35)",
                boxShadow: "0 4px 16px rgba(245,175,25,0.15)",
                background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
              }}
            >
              {!vendor.profilePhotoUrl && (
                <Storefront sx={{ fontSize: 32, color: "white" }} />
              )}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#1a1a1a",
                  mb: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,

                }}
              >
                {vendor.name}
                {/* {vendor.isVerified && (
                  <Verified sx={{ fontSize: 15, color: "#e6ba54" }} />
                )} */}
              </Typography>

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LocationOn sx={{ fontSize: 13, color: "#555" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#555", fontSize: "0.72rem" }}
                >
                  {vendor.address?.city}, {vendor.address?.postalCode}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Divider sx={{ my: 1.5, borderColor: "rgba(0,0,0,0.07)" }} />

          {/* Featured Products Slider */}
          {featuredProducts.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.5,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  color: "#999",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                }}
              >
                <FlashOn sx={{ fontSize: 15, color: "#e6ba54" }} />
                Featured Products
              </Typography>

              <Slider {...settings}>
                {featuredProducts.map((product) => (
                  <Box key={product.uuid} sx={{ px: 0.5 }}>
                    <ProductPreview
                      product={product}
                      onProductClick={onProductClick}
                    />
                  </Box>
                ))}
              </Slider>
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ p: 2.5, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<ShoppingBag />}
            onClick={() => onVendorClick(vendor.id, vendor.name)}
            sx={{
              background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 800,
              color: "#0f0f0f",
              py: 1.4,
              fontSize: "0.9rem",
              letterSpacing: "0.2px",
              boxShadow: "none",
              "&:hover": {
                background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
                filter: "brightness(1.08)",
                transform: "translateY(-2px)",
                boxShadow: "0 12px 28px rgba(245,175,25,0.35)",
              },
            }}
          >
            Shop Now
          </Button>
        </CardActions>
      </VendorCard>
    </Zoom>
  );
};

// ─── Custom Slider Arrows ───────────────────────────────────────────────────

const CustomPrevArrow = (props) => {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        left: -10,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        background: "#ffffff",
        border: "1px solid rgba(245,175,25,0.25)",
        color: "#e6ba54",
        "&:hover": {
          background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
          color: "white",
          transform: "translateY(-50%) scale(1.1)",
          borderColor: "transparent",
        },
      }}
      size="small"
    >
      <ChevronLeft />
    </IconButton>
  );
};

const CustomNextArrow = (props) => {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        right: -10,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        background: "#ffffff",
        border: "1px solid rgba(245,175,25,0.25)",
        color: "#e6ba54",
        "&:hover": {
          background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
          color: "white",
          transform: "translateY(-50%) scale(1.1)",
          borderColor: "transparent",
        },
      }}
      size="small"
    >
      <ChevronRight />
    </IconButton>
  );
};

// ─── Main Vendor Showcase Component ─────────────────────────────────────────

const VendorShowcase = ({ postalCode, onVendorSelect, onProductSelect }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const loadMoreTriggerRef = useRef(null);
  
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalVendors, setTotalVendors] = useState(0);
  const [locationInfo, setLocationInfo] = useState(null);

  const fetchVendors = useCallback(
    async (page, isLoadMore = false) => {
      if (!postalCode) {
        console.log("❌ No postal code provided");
        setError("No location selected");
        setLoading(false);
        return;
      }

      console.log(`🔄 fetchVendors called - Page: ${page}, isLoadMore: ${isLoadMore}`);

      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        console.log(`📡 Making API call for page ${page}...`);
        const response = await NearByVendors({
          postalCode,
          page,
          limit: 10,
        });

        console.log("✅ API Response received:", response.data);

        if (response.data.success) {
          const {
            vendors: newVendors,
            pagination: pag,
            locationInfo: locInfo,
          } = response.data.data;

          console.log(`📦 Received ${newVendors.length} vendors for page ${page}`);
          console.log(`📊 Pagination - Page: ${pag.page}, HasNext: ${pag.hasNext}, Total: ${pag.total}`);

          if (isLoadMore) {
            setVendors((prev) => {
              const updated = [...prev, ...newVendors];
              console.log(`📈 Total vendors after append: ${updated.length}`);
              return updated;
            });
          } else {
            setVendors(newVendors);
            console.log(`🎯 Initial load: ${newVendors.length} vendors`);
          }

          setCurrentPage(pag.page);
          setHasMore(pag.hasNext);
          setTotalVendors(pag.total);
          setLocationInfo(locInfo);
          setError(null);
        } else {
          console.error("❌ API returned success: false", response.data.message);
          setError(response.data.message || "Failed to fetch vendors");
        }
      } catch (err) {
        console.error("❌ Error fetching vendors:", err);
        setError(
          err.response?.data?.message || "Network error. Please try again.",
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [postalCode],
  );

  // Initial fetch when postalCode changes
  useEffect(() => {
    if (postalCode) {
      console.log("🚀 Initializing vendor showcase for postalCode:", postalCode);
      setVendors([]);
      setCurrentPage(1);
      setHasMore(true);
      setLoadingMore(false);
      fetchVendors(1, false);
    }
  }, [postalCode, fetchVendors]);

  // Setup Intersection Observer for infinite scroll
  useEffect(() => {
    const triggerElement = loadMoreTriggerRef.current;
    
    if (!triggerElement) {
      console.log("⚠️ Trigger element not found in DOM");
      return;
    }

    if (!hasMore) {
      console.log("🏁 No more data to load, skipping observer");
      return;
    }

    if (loadingMore || loading) {
      console.log("⏸️ Currently loading, skipping observer setup");
      return;
    }

    console.log("👁️ Setting up Intersection Observer for infinite scroll");

    const options = {
      root: null,
      rootMargin: "0px 0px 200px 0px",
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        console.log(`🎯 Observer entry - isIntersecting: ${entry.isIntersecting}, ` +
                   `intersectionRatio: ${entry.intersectionRatio}`);
        
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          const nextPage = currentPage + 1;
          console.log(`📄 Triggering load for page ${nextPage}...`);
          fetchVendors(nextPage, true);
        }
      });
    }, options);

    observer.observe(triggerElement);
    console.log("✅ Observer attached to trigger element");

    return () => {
      console.log("🔌 Disconnecting observer");
      observer.disconnect();
    };
  }, [hasMore, loadingMore, loading, currentPage, fetchVendors, vendors.length]);

  const handleVendorClick = (vendorId, vendorName) => {
    console.log("Selected vendor:", vendorId, vendorName);
    navigate(`/vendor/${vendorId}`, {
      state: { vendorName: vendorName },
    });
  };

  const handleProductClick = (productUuid) => {
    console.log("Selected product:", productUuid);
    if (onProductSelect) {
      onProductSelect(productUuid);
    }
  };

  const handleManualLoadMore = () => {
    if (hasMore && !loadingMore && !loading) {
      const nextPage = currentPage + 1;
      console.log(`🖱️ Manual load - Loading page ${nextPage}...`);
      fetchVendors(nextPage, true);
    } else {
      console.log("⚠️ Cannot load more:", { hasMore, loadingMore, loading });
    }
  };

  // Loading skeleton with fixed card sizes
  if (loading && vendors.length === 0) {
    return (
      <Box sx={{ bgcolor: "#f9f9f9", py:  3 }}>
        <Container maxWidth="xl">
          <SectionTitle>
            <Typography variant="h2">
              Nearby <span>Vendors</span>
            </Typography>
            <Typography className="subtitle">
              Discover fresh groceries from local stores near you
            </Typography>
          </SectionTitle>

          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Card
                  sx={{
                    borderRadius: 4,
                    bgcolor: "#ffffff",
                    border: "1px solid rgba(0,0,0,0.07)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                    height: "100%",
                    minHeight: 380,
                    width: 400
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    height={140}
                    sx={{ bgcolor: "rgba(0,0,0,0.04)" }}
                  />
                  <CardContent>
                    <Skeleton
                      variant="text"
                      width="80%"
                      sx={{ bgcolor: "rgba(0,0,0,0.04)" }}
                    />
                    <Skeleton
                      variant="text"
                      width="60%"
                      sx={{ bgcolor: "rgba(0,0,0,0.04)" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={80}
                      sx={{ mt: 2, bgcolor: "rgba(0,0,0,0.04)" }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error && vendors.length === 0) {
    return (
      <Box sx={{ bgcolor: "#f9f9f9", py: 8 }}>
        <Container maxWidth="xl" sx={{ textAlign: "center" }}>
          <Paper
            sx={{
              p: 6,
              borderRadius: 4,
              background: "#ffffff",
              border: "1px solid rgba(241,39,17,0.12)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.07)",
            }}
          >
            <Storefront
              sx={{ fontSize: 80, color: "rgba(245,175,25,0.5)", mb: 2 }}
            />
            <Typography
              variant="h5"
              gutterBottom
              fontWeight={800}
              color="#1a1a1a"
            >
              Oops! Something went wrong
            </Typography>
            <Typography color="#888" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => fetchVendors(1, false)}
              sx={{
                borderRadius: 3,
                background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
                color: "#0f0f0f",
                fontWeight: 800,
                textTransform: "none",
                px: 4,
                py: 1.5,
              }}
            >
              Try Again
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Empty state
  if (!loading && vendors.length === 0) {
    return (
      <Box sx={{ bgcolor: "#f9f9f9", py: 8 }}>
        <Container maxWidth="xl" sx={{ textAlign: "center" }}>
          <Paper
            sx={{
              p: 6,
              borderRadius: 4,
              background: "#ffffff",
              border: "1px solid rgba(0,0,0,0.07)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
            }}
          >
            <Inventory
              sx={{ fontSize: 80, color: "rgba(245,175,25,0.5)", mb: 2 }}
            />
            <Typography
              variant="h5"
              gutterBottom
              fontWeight={800}
              color="#1a1a1a"
            >
              No Vendors Found
            </Typography>
            <Typography color="#888">
              We couldn't find any vendors near your location. Try changing your
              delivery address.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#f9f9f9", py: 3 }}>
      <Container maxWidth="xl">
        <SectionTitle>
          <Typography variant="h2">
            Nearby <span>Vendors</span>
          </Typography>
          <Typography className="subtitle">
            Discover fresh groceries from local stores near you
          </Typography>
        </SectionTitle>

        {/* Stats Bar */}
        {totalVendors > 0 && (
          <Box sx={{ mb: 3, textAlign: "right" }}>
            {/* <Typography variant="body2" sx={{ color: "#666" }}>
              Showing {vendors.length} of {totalVendors} vendors
            </Typography> */}
          </Box>
        )}

        {/* Vendors Grid with fixed card sizes */}
        <AnimatePresence>
          <Grid container spacing={3}>
            {vendors.map((vendor, idx) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                lg={3} 
                key={`${vendor.id}-${idx}`}
                sx={{ display: "flex" }}
              >
                <Box sx={{ width: "100%", height: "100%" }}>
                  <VendorCardComponent
                    vendor={vendor}
                    index={idx}
                    onVendorClick={(id, name) => handleVendorClick(id, name)}
                    onProductClick={handleProductClick}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </AnimatePresence>

        {/* Loading More Indicator */}
        {loadingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4, py: 3 }}>
            <CircularProgress sx={{ color: "#e6ba54" }} />
            <Typography sx={{ ml: 2, color: "#666" }}>
              Loading more vendors...
            </Typography>
          </Box>
        )}

        {/* Intersection Observer Trigger Element */}
        {hasMore && !loadingMore && (
          <Box 
            ref={loadMoreTriggerRef}
            sx={{ 
              height: "80px", 
              mt: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(245,175,25,0.05)",
              borderRadius: "12px",
              border: "1px dashed rgba(245,175,25,0.3)"
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: "#e6ba54",
                fontSize: "0.9rem",
                fontWeight: 600,
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 0.6 },
                  "50%": { opacity: 1 },
                  "100%": { opacity: 0.6 }
                }
              }}
            >
              ↓ Scroll down to load more vendors ↓
            </Typography>
          </Box>
        )}

        

        {/* Manual Load More Button */}
        {hasMore && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleManualLoadMore}
              disabled={loadingMore}
              sx={{
                borderRadius: "14px",
                px: 5,
                py: 1.5,
                background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
                color: "#0f0f0f",
                fontWeight: 800,
                textTransform: "none",
                fontSize: "0.95rem",
                "&:hover": {
                  background: "linear-gradient(135deg, #e6ba54 0%, #e6ba54 100%)",
                  filter: "brightness(1.08)",
                  transform: "translateY(-2px)",
                },
                "&.Mui-disabled": {
                  background: "#ccc",
                  color: "#666"
                }
              }}
            >
              {loadingMore ? "Loading..." : "Load More Vendors"}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default VendorShowcase;